import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle } from
'@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle } from
'@/components/ui/alert-dialog';
import {
  Presentation,
  Play,
  Edit,
  Trash2,
  Plus,
  Search,
  Calendar,
  Filter,
  Download,
  Loader2,
  Grid3X3,
  List,
  SortAsc,
  SortDesc,
  FileText,
  Eye,
  Maximize2 } from
'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'motion/react';
import EnhancedPresentationView from './EnhancedPresentationView';
import FullscreenPresentationView from './FullscreenPresentationView';

interface PresentationData {
  id: string;
  teacher_id: number;
  title: string;
  description: string;
  subject: string;
  grade_level: string;
  slides_data: string;
  template_theme: string;
  total_slides: number;
  created_at: string;
  updated_at: string;
}

interface ProcessedSlide {
  id: string;
  title: string;
  content: string | string[];
  layoutType: string;
  processedContent: any;
  designApplied: boolean;
  type: 'title' | 'content' | 'image' | 'list' | 'conclusion' | 'quote' | 'comparison' | 'image-text';
  notes?: string;
  imageUrl?: string;
  imageAlt?: string;
}

interface Template {
  id: string;
  name: string;
  preview: {
    titleSlide: {
      backgroundColor: string;
      textColor: string;
      accentColor: string;
    };
    contentSlide: {
      backgroundColor: string;
      textColor: string;
      accentColor: string;
    };
    fonts: {
      heading: string;
      body: string;
    };
  };
  colorScheme: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
}

interface PresentationManagerProps {
  onCreateNew?: () => void;
  onEditPresentation?: (presentation: PresentationData) => void;
}

type ViewMode = 'grid' | 'list';
type SortBy = 'created' | 'updated' | 'title' | 'slides';
type SortOrder = 'asc' | 'desc';

// Enhanced template themes with proper preview structure
const DEFAULT_TEMPLATES: Template[] = [
{
  id: 'professional-blue',
  name: 'Professional Blue',
  preview: {
    titleSlide: {
      backgroundColor: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
      textColor: '#ffffff',
      accentColor: '#60a5fa'
    },
    contentSlide: {
      backgroundColor: '#ffffff',
      textColor: '#1e293b',
      accentColor: '#3b82f6'
    },
    fonts: {
      heading: 'Inter, system-ui, sans-serif',
      body: 'Inter, system-ui, sans-serif'
    }
  },
  colorScheme: {
    primary: '#1e3a8a',
    secondary: '#3b82f6',
    accent: '#60a5fa',
    background: '#ffffff'
  }
},
{
  id: 'modern-purple',
  name: 'Modern Purple',
  preview: {
    titleSlide: {
      backgroundColor: 'linear-gradient(135deg, #6b21a8, #8b5cf6)',
      textColor: '#ffffff',
      accentColor: '#a78bfa'
    },
    contentSlide: {
      backgroundColor: '#ffffff',
      textColor: '#1e293b',
      accentColor: '#8b5cf6'
    },
    fonts: {
      heading: 'Inter, system-ui, sans-serif',
      body: 'Inter, system-ui, sans-serif'
    }
  },
  colorScheme: {
    primary: '#6b21a8',
    secondary: '#8b5cf6',
    accent: '#a78bfa',
    background: '#ffffff'
  }
},
{
  id: 'corporate-green',
  name: 'Corporate Green',
  preview: {
    titleSlide: {
      backgroundColor: 'linear-gradient(135deg, #065f46, #10b981)',
      textColor: '#ffffff',
      accentColor: '#34d399'
    },
    contentSlide: {
      backgroundColor: '#ffffff',
      textColor: '#1e293b',
      accentColor: '#10b981'
    },
    fonts: {
      heading: 'Inter, system-ui, sans-serif',
      body: 'Inter, system-ui, sans-serif'
    }
  },
  colorScheme: {
    primary: '#065f46',
    secondary: '#10b981',
    accent: '#34d399',
    background: '#ffffff'
  }
}];


const PresentationManager: React.FC<PresentationManagerProps> = ({
  onCreateNew,
  onEditPresentation
}) => {
  const [presentations, setPresentations] = useState<PresentationData[]>([]);
  const [filteredPresentations, setFilteredPresentations] = useState<PresentationData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortBy>('created');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Dialog states
  const [presentationToDelete, setPresentationToDelete] = useState<PresentationData | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeletingPresentation, setIsDeletingPresentation] = useState(false);

  // Presentation viewing
  const [currentPresentation, setCurrentPresentation] = useState<{
    data: PresentationData;
    slides: ProcessedSlide[];
    template: Template;
  } | null>(null);
  const [showPresentationView, setShowPresentationView] = useState(false);
  const [isFullscreenMode, setIsFullscreenMode] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user?.ID) {
      loadPresentations();
    }
  }, [user]);

  useEffect(() => {
    filterAndSortPresentations();
  }, [presentations, searchTerm, subjectFilter, gradeFilter, sortBy, sortOrder]);

  const loadPresentations = async () => {
    if (!user?.ID) return;

    try {
      setIsLoading(true);
      const { data, error } = await window.ezsite.apis.tablePage(15255, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'id',
        IsAsc: false,
        Filters: [{
          name: 'teacher_id',
          op: 'Equal',
          value: user.ID
        }]
      });

      if (error) throw error;
      setPresentations(data?.List || []);
    } catch (error) {
      console.error('Error loading presentations:', error);
      toast({
        title: "Error",
        description: "Failed to load presentations. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortPresentations = () => {
    let filtered = [...presentations];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((p) =>
      p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.subject.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply subject filter
    if (subjectFilter !== 'all') {
      filtered = filtered.filter((p) => p.subject === subjectFilter);
    }

    // Apply grade filter
    if (gradeFilter !== 'all') {
      filtered = filtered.filter((p) => p.grade_level === gradeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'slides':
          aValue = a.total_slides;
          bValue = b.total_slides;
          break;
        case 'updated':
          aValue = new Date(a.updated_at);
          bValue = new Date(b.updated_at);
          break;
        case 'created':
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredPresentations(filtered);
  };

  const handleDeletePresentation = (presentation: PresentationData) => {
    setPresentationToDelete(presentation);
    setShowDeleteDialog(true);
  };

  const confirmDeletePresentation = async () => {
    if (!presentationToDelete) return;

    try {
      setIsDeletingPresentation(true);

      const { error } = await window.ezsite.apis.tableDelete(15255, {
        ID: parseInt(presentationToDelete.id)
      });

      if (error) throw error;

      // Remove from local state
      setPresentations((prev) => prev.filter((p) => p.id !== presentationToDelete.id));

      toast({
        title: "Presentation Deleted",
        description: `"${presentationToDelete.title}" has been successfully deleted.`
      });

    } catch (error) {
      console.error('Error deleting presentation:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete presentation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeletingPresentation(false);
      setShowDeleteDialog(false);
      setPresentationToDelete(null);
    }
  };

  const processSlides = (slidesData: string): ProcessedSlide[] => {
    try {
      const rawSlides = JSON.parse(slidesData);
      return rawSlides.map((slide: any, index: number) => ({
        id: slide.id || `slide-${index}`,
        title: slide.title || `Slide ${index + 1}`,
        content: slide.content || [],
        layoutType: slide.layoutType || slide.type || 'content',
        processedContent: slide.processedContent || {},
        designApplied: slide.designApplied || false,
        type: slide.type || 'content',
        notes: slide.notes,
        imageUrl: slide.imageUrl,
        imageAlt: slide.imageAlt
      }));
    } catch (error) {
      console.error('Error processing slides:', error);
      return [];
    }
  };

  const handleViewPresentation = async (presentation: PresentationData) => {
    try {
      // Parse slides data with error handling
      const slides = processSlides(presentation.slides_data);
      const template = DEFAULT_TEMPLATES.find((t) =>
      t.name === presentation.template_theme ||
      t.id === presentation.template_theme
      ) || DEFAULT_TEMPLATES[0];

      setCurrentPresentation({
        data: presentation,
        slides,
        template
      });
      setShowPresentationView(true);
    } catch (error) {
      console.error('Error loading presentation:', error);
      toast({
        title: "Load Error",
        description: "Failed to load presentation data.",
        variant: "destructive"
      });
    }
  };

  const handleFullscreenPresentation = async (presentation: PresentationData) => {
    try {
      const slides = processSlides(presentation.slides_data);
      const template = DEFAULT_TEMPLATES.find((t) =>
      t.name === presentation.template_theme ||
      t.id === presentation.template_theme
      ) || DEFAULT_TEMPLATES[0];

      setCurrentPresentation({
        data: presentation,
        slides,
        template
      });
      setIsFullscreenMode(true);
    } catch (error) {
      console.error('Error loading presentation for fullscreen:', error);
      toast({
        title: "Load Error",
        description: "Failed to load presentation for fullscreen mode.",
        variant: "destructive"
      });
    }
  };

  const exportToPDF = async (presentation: PresentationData) => {
    try {
      const slides = processSlides(presentation.slides_data);

      toast({
        title: "Exporting PDF",
        description: "Please wait while we generate your PDF..."
      });

      // Simulate PDF export for now
      await new Promise((resolve) => setTimeout(resolve, 2000));

      toast({
        title: "PDF Exported!",
        description: `Presentation exported as ${presentation.title}.pdf`
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export presentation to PDF.",
        variant: "destructive"
      });
    }
  };

  // Get unique subjects and grades for filters
  const uniqueSubjects = [...new Set(presentations.map((p) => p.subject).filter(Boolean))];
  const uniqueGrades = [...new Set(presentations.map((p) => p.grade_level).filter(Boolean))];

  // Fullscreen presentation mode
  if (isFullscreenMode && currentPresentation) {
    return (
      <FullscreenPresentationView
        slides={currentPresentation.slides}
        template={currentPresentation.template}
        title={currentPresentation.data.title}
        onClose={() => setIsFullscreenMode(false)}
        autoplay={false} data-id="mv2gb96ds" data-path="src/components/ai/PresentationManager.tsx" />);


  }

  // Enhanced presentation view mode
  if (showPresentationView && currentPresentation) {
    return (
      <EnhancedPresentationView
        slides={currentPresentation.slides}
        template={currentPresentation.template}
        title={currentPresentation.data.title}
        onEdit={() => {
          setShowPresentationView(false);
          onEditPresentation?.(currentPresentation.data);
        }}
        onDownload={() => exportToPDF(currentPresentation.data)}
        onSave={async () => {
          // Implement save functionality
          toast({
            title: "Saved",
            description: "Presentation has been saved."
          });
        }} data-id="szjxk4gfg" data-path="src/components/ai/PresentationManager.tsx" />);


  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6" data-id="n2jvsr4zz" data-path="src/components/ai/PresentationManager.tsx">

      {/* Header */}
      <div className="flex items-center justify-between" data-id="sncc2hii5" data-path="src/components/ai/PresentationManager.tsx">
        <div data-id="sylvqi5oa" data-path="src/components/ai/PresentationManager.tsx">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3" data-id="grv1rri16" data-path="src/components/ai/PresentationManager.tsx">
            <Presentation className="h-8 w-8 text-blue-600" data-id="5rjthvu5i" data-path="src/components/ai/PresentationManager.tsx" />
            My Presentations
          </h1>
          <p className="text-gray-600 mt-2" data-id="zimlc0gp4" data-path="src/components/ai/PresentationManager.tsx">
            Manage your AI-generated presentations
          </p>
        </div>
        <Button onClick={onCreateNew} className="bg-blue-600 hover:bg-blue-700" data-id="s5q97vx7l" data-path="src/components/ai/PresentationManager.tsx">
          <Plus className="h-4 w-4 mr-2" data-id="zha7zywqb" data-path="src/components/ai/PresentationManager.tsx" />
          Create New
        </Button>
      </div>

      {/* Filters and Search */}
      <Card data-id="dskoq39d8" data-path="src/components/ai/PresentationManager.tsx">
        <CardContent className="p-4" data-id="kookj7z1p" data-path="src/components/ai/PresentationManager.tsx">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center" data-id="ddu1uih8w" data-path="src/components/ai/PresentationManager.tsx">
            <div className="flex-1" data-id="bt08khi2t" data-path="src/components/ai/PresentationManager.tsx">
              <div className="relative" data-id="irxscg8i7" data-path="src/components/ai/PresentationManager.tsx">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" data-id="q4xovod0s" data-path="src/components/ai/PresentationManager.tsx" />
                <Input
                  placeholder="Search presentations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10" data-id="61fmnazkh" data-path="src/components/ai/PresentationManager.tsx" />

              </div>
            </div>
            
            <div className="flex gap-2 flex-wrap" data-id="tfazguk27" data-path="src/components/ai/PresentationManager.tsx">
              <Select value={subjectFilter} onValueChange={setSubjectFilter} data-id="bv7jeg6y4" data-path="src/components/ai/PresentationManager.tsx">
                <SelectTrigger className="w-32" data-id="kt46meaml" data-path="src/components/ai/PresentationManager.tsx">
                  <SelectValue placeholder="Subject" data-id="itvi4d3gv" data-path="src/components/ai/PresentationManager.tsx" />
                </SelectTrigger>
                <SelectContent data-id="30b4irdn4" data-path="src/components/ai/PresentationManager.tsx">
                  <SelectItem value="all" data-id="uugs9rx4w" data-path="src/components/ai/PresentationManager.tsx">All Subjects</SelectItem>
                  {uniqueSubjects.map((subject) =>
                  <SelectItem key={subject} value={subject} data-id="fb9llx7qd" data-path="src/components/ai/PresentationManager.tsx">{subject}</SelectItem>
                  )}
                </SelectContent>
              </Select>

              <Select value={gradeFilter} onValueChange={setGradeFilter} data-id="afhu2j9s5" data-path="src/components/ai/PresentationManager.tsx">
                <SelectTrigger className="w-32" data-id="1z52tjpop" data-path="src/components/ai/PresentationManager.tsx">
                  <SelectValue placeholder="Grade" data-id="s4bhmzpts" data-path="src/components/ai/PresentationManager.tsx" />
                </SelectTrigger>
                <SelectContent data-id="shhj6pa9m" data-path="src/components/ai/PresentationManager.tsx">
                  <SelectItem value="all" data-id="vrf6k1sxf" data-path="src/components/ai/PresentationManager.tsx">All Grades</SelectItem>
                  {uniqueGrades.map((grade) =>
                  <SelectItem key={grade} value={grade} data-id="4s4yfbtos" data-path="src/components/ai/PresentationManager.tsx">{grade}</SelectItem>
                  )}
                </SelectContent>
              </Select>

              <Select value={`${sortBy}-${sortOrder}`} onValueChange={(value) => {
                const [newSortBy, newSortOrder] = value.split('-');
                setSortBy(newSortBy as SortBy);
                setSortOrder(newSortOrder as SortOrder);
              }} data-id="7lnw7phr9" data-path="src/components/ai/PresentationManager.tsx">
                <SelectTrigger className="w-40" data-id="sug93qcx6" data-path="src/components/ai/PresentationManager.tsx">
                  <SelectValue data-id="i9kt3gwin" data-path="src/components/ai/PresentationManager.tsx" />
                </SelectTrigger>
                <SelectContent data-id="othb0uko5" data-path="src/components/ai/PresentationManager.tsx">
                  <SelectItem value="created-desc" data-id="ojrv5ts7r" data-path="src/components/ai/PresentationManager.tsx">Newest First</SelectItem>
                  <SelectItem value="created-asc" data-id="6z1dl4ju3" data-path="src/components/ai/PresentationManager.tsx">Oldest First</SelectItem>
                  <SelectItem value="updated-desc" data-id="414u1sjmu" data-path="src/components/ai/PresentationManager.tsx">Recently Updated</SelectItem>
                  <SelectItem value="title-asc" data-id="xnrzfvt7b" data-path="src/components/ai/PresentationManager.tsx">Title A-Z</SelectItem>
                  <SelectItem value="title-desc" data-id="ftut9zaxr" data-path="src/components/ai/PresentationManager.tsx">Title Z-A</SelectItem>
                  <SelectItem value="slides-desc" data-id="in66ox88a" data-path="src/components/ai/PresentationManager.tsx">Most Slides</SelectItem>
                  <SelectItem value="slides-asc" data-id="xdmq2tgwe" data-path="src/components/ai/PresentationManager.tsx">Fewest Slides</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex border rounded-md" data-id="us8wwafo8" data-path="src/components/ai/PresentationManager.tsx">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="rounded-r-none" data-id="ci8dekj1i" data-path="src/components/ai/PresentationManager.tsx">

                  <Grid3X3 className="h-4 w-4" data-id="i0igjucb4" data-path="src/components/ai/PresentationManager.tsx" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none" data-id="4xhz1w4yg" data-path="src/components/ai/PresentationManager.tsx">

                  <List className="h-4 w-4" data-id="awgmgiqqu" data-path="src/components/ai/PresentationManager.tsx" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600" data-id="c4k6z78xg" data-path="src/components/ai/PresentationManager.tsx">
        <span data-id="5gc48lhr5" data-path="src/components/ai/PresentationManager.tsx">
          {filteredPresentations.length} of {presentations.length} presentations
        </span>
        {(searchTerm || subjectFilter !== 'all' || gradeFilter !== 'all') &&
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSearchTerm('');
            setSubjectFilter('all');
            setGradeFilter('all');
          }} data-id="zj8llxs7o" data-path="src/components/ai/PresentationManager.tsx">

            Clear Filters
          </Button>
        }
      </div>

      {/* Content */}
      {isLoading ?
      <div className="flex items-center justify-center py-12" data-id="nswfn9uuc" data-path="src/components/ai/PresentationManager.tsx">
          <div className="text-center" data-id="gixt9jdrj" data-path="src/components/ai/PresentationManager.tsx">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" data-id="28vwlk0og" data-path="src/components/ai/PresentationManager.tsx" />
            <p className="text-gray-600" data-id="n76ekrybx" data-path="src/components/ai/PresentationManager.tsx">Loading presentations...</p>
          </div>
        </div> :
      filteredPresentations.length === 0 ?
      <Card data-id="3ki9zdlml" data-path="src/components/ai/PresentationManager.tsx">
          <CardContent className="p-12 text-center" data-id="zc9fcu5vp" data-path="src/components/ai/PresentationManager.tsx">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="9ap7yslcf" data-path="src/components/ai/PresentationManager.tsx" />
            <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="qh7248mh4" data-path="src/components/ai/PresentationManager.tsx">
              {presentations.length === 0 ? 'No Presentations Yet' : 'No Results Found'}
            </h3>
            <p className="text-gray-600 mb-4" data-id="af3i8bu08" data-path="src/components/ai/PresentationManager.tsx">
              {presentations.length === 0 ?
            'Create your first AI-powered presentation to get started.' :
            'Try adjusting your search terms or filters.'
            }
            </p>
            {presentations.length === 0 &&
          <Button onClick={onCreateNew} data-id="6kgxmhxqv" data-path="src/components/ai/PresentationManager.tsx">
                <Plus className="h-4 w-4 mr-2" data-id="rnby12tcy" data-path="src/components/ai/PresentationManager.tsx" />
                Create First Presentation
              </Button>
          }
          </CardContent>
        </Card> :

      <AnimatePresence data-id="l24726c78" data-path="src/components/ai/PresentationManager.tsx">
          {viewMode === 'grid' ?
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="kel0pn62w" data-path="src/components/ai/PresentationManager.tsx">
              {filteredPresentations.map((presentation, index) =>
          <motion.div
            key={presentation.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }} data-id="akx3go334" data-path="src/components/ai/PresentationManager.tsx">

                  <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer group" data-id="0knlrlhvn" data-path="src/components/ai/PresentationManager.tsx">
                    <CardHeader className="pb-3" data-id="tg2yoc7kg" data-path="src/components/ai/PresentationManager.tsx">
                      <div className="flex items-start justify-between" data-id="argc2lwl7" data-path="src/components/ai/PresentationManager.tsx">
                        <div className="flex-1 min-w-0" data-id="9r6jp9j23" data-path="src/components/ai/PresentationManager.tsx">
                          <CardTitle className="text-lg truncate group-hover:text-blue-600 transition-colors" data-id="eizc55wkz" data-path="src/components/ai/PresentationManager.tsx">
                            {presentation.title}
                          </CardTitle>
                          <CardDescription className="line-clamp-2 mt-1" data-id="375kkdpub" data-path="src/components/ai/PresentationManager.tsx">
                            {presentation.description}
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className="ml-2 flex-shrink-0" data-id="0hqk7tlt0" data-path="src/components/ai/PresentationManager.tsx">
                          {presentation.total_slides} slides
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent data-id="gzdm5kmqc" data-path="src/components/ai/PresentationManager.tsx">
                      <div className="space-y-3" data-id="6bpdiejn5" data-path="src/components/ai/PresentationManager.tsx">
                        <div className="flex gap-2" data-id="do1yow8sa" data-path="src/components/ai/PresentationManager.tsx">
                          {presentation.subject &&
                    <Badge variant="secondary" className="text-xs" data-id="ada9ufiwl" data-path="src/components/ai/PresentationManager.tsx">
                              {presentation.subject}
                            </Badge>
                    }
                          {presentation.grade_level &&
                    <Badge variant="outline" className="text-xs" data-id="z7h09jmia" data-path="src/components/ai/PresentationManager.tsx">
                              {presentation.grade_level}
                            </Badge>
                    }
                        </div>
                        
                        <div className="text-xs text-gray-500" data-id="itfwvmnwt" data-path="src/components/ai/PresentationManager.tsx">
                          <div data-id="b2jnlrce3" data-path="src/components/ai/PresentationManager.tsx">Created: {new Date(presentation.created_at).toLocaleDateString()}</div>
                          <div data-id="8o0z3arvz" data-path="src/components/ai/PresentationManager.tsx">Updated: {new Date(presentation.updated_at).toLocaleDateString()}</div>
                        </div>
                        
                        <div className="flex gap-2 pt-2" data-id="1rf378toz" data-path="src/components/ai/PresentationManager.tsx">
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewPresentation(presentation)}
                      className="flex-1" data-id="bpad3292z" data-path="src/components/ai/PresentationManager.tsx">

                            <Eye className="h-3 w-3 mr-1" data-id="vft23eu1s" data-path="src/components/ai/PresentationManager.tsx" />
                            View
                          </Button>
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleFullscreenPresentation(presentation)}
                      title="Present in Fullscreen" data-id="ak9zf1zzg" data-path="src/components/ai/PresentationManager.tsx">

                            <Maximize2 className="h-3 w-3" data-id="uv89dl4br" data-path="src/components/ai/PresentationManager.tsx" />
                          </Button>
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEditPresentation?.(presentation)} data-id="lejvy0vrl" data-path="src/components/ai/PresentationManager.tsx">

                            <Edit className="h-3 w-3" data-id="ktjwestp9" data-path="src/components/ai/PresentationManager.tsx" />
                          </Button>
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => exportToPDF(presentation)} data-id="53q8zrvbl" data-path="src/components/ai/PresentationManager.tsx">

                            <Download className="h-3 w-3" data-id="raan8tdho" data-path="src/components/ai/PresentationManager.tsx" />
                          </Button>
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePresentation(presentation);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="4460fwmiq" data-path="src/components/ai/PresentationManager.tsx">

                            <Trash2 className="h-3 w-3" data-id="avwg6145d" data-path="src/components/ai/PresentationManager.tsx" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
          )}
            </div> :

        <Card data-id="qmrxnlgvw" data-path="src/components/ai/PresentationManager.tsx">
              <CardContent className="p-0" data-id="coghy8g6b" data-path="src/components/ai/PresentationManager.tsx">
                <div className="divide-y" data-id="5ac2yynx3" data-path="src/components/ai/PresentationManager.tsx">
                  {filteredPresentations.map((presentation, index) =>
              <motion.div
                key={presentation.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="p-4 hover:bg-gray-50 transition-colors" data-id="nizl1bfdb" data-path="src/components/ai/PresentationManager.tsx">

                      <div className="flex items-center justify-between" data-id="7w37luwxw" data-path="src/components/ai/PresentationManager.tsx">
                        <div className="flex-1 min-w-0" data-id="papbu3qel" data-path="src/components/ai/PresentationManager.tsx">
                          <div className="flex items-center gap-3" data-id="2ihaz8axn" data-path="src/components/ai/PresentationManager.tsx">
                            <h3 className="font-semibold text-lg truncate" data-id="t35cbxsb2" data-path="src/components/ai/PresentationManager.tsx">
                              {presentation.title}
                            </h3>
                            <Badge variant="outline" data-id="h0bxe5pfa" data-path="src/components/ai/PresentationManager.tsx">{presentation.total_slides} slides</Badge>
                            {presentation.subject &&
                      <Badge variant="secondary" data-id="2e67ah8qd" data-path="src/components/ai/PresentationManager.tsx">{presentation.subject}</Badge>
                      }
                            {presentation.grade_level &&
                      <Badge variant="outline" data-id="0haorlkan" data-path="src/components/ai/PresentationManager.tsx">{presentation.grade_level}</Badge>
                      }
                          </div>
                          <p className="text-gray-600 text-sm mt-1 line-clamp-1" data-id="k9h4eap91" data-path="src/components/ai/PresentationManager.tsx">
                            {presentation.description}
                          </p>
                          <div className="text-xs text-gray-500 mt-2" data-id="6rvbzzubq" data-path="src/components/ai/PresentationManager.tsx">
                            Created: {new Date(presentation.created_at).toLocaleDateString()} • 
                            Updated: {new Date(presentation.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4" data-id="ds1b7znrp" data-path="src/components/ai/PresentationManager.tsx">
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewPresentation(presentation)} data-id="oclaqignt" data-path="src/components/ai/PresentationManager.tsx">

                            <Eye className="h-4 w-4 mr-1" data-id="fhlzlpbjz" data-path="src/components/ai/PresentationManager.tsx" />
                            View
                          </Button>
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleFullscreenPresentation(presentation)}
                      title="Present in Fullscreen" data-id="k1prm2fv8" data-path="src/components/ai/PresentationManager.tsx">

                            <Maximize2 className="h-4 w-4" data-id="84hzq3qud" data-path="src/components/ai/PresentationManager.tsx" />
                          </Button>
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onEditPresentation?.(presentation)} data-id="rw0z3ptyt" data-path="src/components/ai/PresentationManager.tsx">

                            <Edit className="h-4 w-4" data-id="ceyf9cmgn" data-path="src/components/ai/PresentationManager.tsx" />
                          </Button>
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => exportToPDF(presentation)} data-id="wg1ag18j9" data-path="src/components/ai/PresentationManager.tsx">

                            <Download className="h-4 w-4" data-id="3w6ffk0al" data-path="src/components/ai/PresentationManager.tsx" />
                          </Button>
                          <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeletePresentation(presentation)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50" data-id="7u3wxaez1" data-path="src/components/ai/PresentationManager.tsx">

                            <Trash2 className="h-4 w-4" data-id="94s6ocprp" data-path="src/components/ai/PresentationManager.tsx" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
              )}
                </div>
              </CardContent>
            </Card>
        }
        </AnimatePresence>
      }

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog} data-id="iz7v9of5g" data-path="src/components/ai/PresentationManager.tsx">
        <AlertDialogContent data-id="ocipbf3hm" data-path="src/components/ai/PresentationManager.tsx">
          <AlertDialogHeader data-id="1g79xjag8" data-path="src/components/ai/PresentationManager.tsx">
            <AlertDialogTitle data-id="7v2wase1n" data-path="src/components/ai/PresentationManager.tsx">Delete Presentation</AlertDialogTitle>
            <AlertDialogDescription data-id="iikoarlet" data-path="src/components/ai/PresentationManager.tsx">
              Are you sure you want to delete "{presentationToDelete?.title}"? 
              This action cannot be undone and will permanently remove the presentation 
              and all of its {presentationToDelete?.total_slides} slides.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter data-id="fp9sugtqu" data-path="src/components/ai/PresentationManager.tsx">
            <AlertDialogCancel disabled={isDeletingPresentation} data-id="7sza35z96" data-path="src/components/ai/PresentationManager.tsx">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeletePresentation}
              disabled={isDeletingPresentation}
              className="bg-red-600 hover:bg-red-700" data-id="6jlqhbw24" data-path="src/components/ai/PresentationManager.tsx">

              {isDeletingPresentation ?
              <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" data-id="dc3j894w7" data-path="src/components/ai/PresentationManager.tsx" />
                  Deleting...
                </> :

              <>
                  <Trash2 className="h-4 w-4 mr-2" data-id="24bdgalyp" data-path="src/components/ai/PresentationManager.tsx" />
                  Delete
                </>
              }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>);

};

export default PresentationManager;